import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError } from '@components/Toast';
import LoaderComponent from '@components/LoaderComponent';

import useMultipartFileUpload from '@hooks/useMultipartFileUpload';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { printApiError } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

import ModalContainer from '@/components/modals/ModalContainer';
import CustomDropdown from '@/components/CustomDropdown';
import CommonImage from '@/components/CommonImage';

const AddFilesModal = ({ onCloseModal, fetchProductFile }) => {
	const [fileTypes, setFileTypes] = useState([]);
	const [selectedFileType, setSelectedFileType] = useState(null);
	const dispatch = useDispatch();
	const { id } = useParams();
	const loader = useSelector((store) => store.loaderStore);

	const { uploadFiles, isUploading: isMultipartUploading } = useMultipartFileUpload({
		onSuccess: async () => {
			dispatch(openLoader());
			await fetchProductFile();
			dispatch(closeLoader());
			setSelectedFileType(null);
			onCloseModal();
		},
		showToasts: true
	});

	useEffect(() => {
		if (id) {
			(async () => {
				const { data } = await ApiClient.GET(`${BASE_URL}/entity-file/file-type/PRODUCT`, '', true);
				let tmpData = data?.map((item) => ({ id: item.constant, label: item.name }));
				tmpData = tmpData?.filter((item) =>
					['Pattern', 'Measurement', 'Techpack', 'Others'].includes(item.label)
				);
				setFileTypes(tmpData);
			})();
		}
	}, [id]);

	const processUploadedFiles = async (uploadResults, fileType) => {
		try {
			dispatch(openLoader());
			const documentIdList = uploadResults.map((result) => result?.documentId).filter(Boolean);

			if (documentIdList.length > 0) {
				await ApiClient.POST('uploadEntityFile', {
					fileType: fileType,
					entityId: id,
					entityType: 'PRODUCT',
					isLink: false,
					documentIdList: documentIdList
				});
				fetchProductFile();
			}
		} catch (error) {
			toastError(error?.data?.message || 'Failed to create file records in database');
		} finally {
			dispatch(closeLoader());
		}
	};

	const getDocumentType = (fileType) => {
		switch (fileType) {
			case 'IMAGE':
				return 'PRODUCT_IMAGE';
			case 'DOCUMENT':
				return 'PRODUCT_DOCUMENT';
			default:
				return 'PRODUCT_NOT_IMAGE';
		}
	};

	const onUploadFile = async (e, newlyUploadedFileType = null) => {
		const files = Array.from(e.target.files);
		if (!files.length) return;
		const documentType = getDocumentType(newlyUploadedFileType || selectedFileType.id);
		try {
			const results = await uploadFiles(files, documentType);

			if (results && results.length > 0) {
				await processUploadedFiles(results, newlyUploadedFileType || selectedFileType.id);
			}
		} catch (error) {
			printApiError(error);
		}
	};

	return (
		<ModalContainer className="costing-add-files-modal" onCloseModal={onCloseModal}>
			<LoaderComponent loading={isMultipartUploading || loader}>
				<div className="popup-wrapper">
					<div className="common-popup costing-add-files-modal">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Add Files</h3>
							</div>
							<div className="close-btn" onClick={onCloseModal}>
								<CommonImage alt="close" src="/icons/close.svg" />
							</div>
						</div>
						<div className="common-popup-body border-bottom">
							<div className="row">
								<div className="col-md-12">
									<div className="form-group upload-and-link">
										<label htmlFor="collection">File Type</label>
										<div className="file-type-dropdown-and-upload">
											<div className="d-flex mb-12">
												<div className="file-type-dropdown w-50 mr-2">
													<CustomDropdown
														title="File type*"
														onItemClick={(value) => {
															setSelectedFileType(value);
														}}
														items={fileTypes?.map((item) => ({
															id: item.id,
															value: item.label
														}))}
														selectedItem={selectedFileType?.value}
													/>
												</div>
												<div className="category-wise-upload d-flex align-items-center w-50">
													<div className="text-view-uploader upload-template w-100">
														<label
															htmlFor="templateUpload"
															className="drag-upload cursor-pointer py-2"
														>
															<span className="custom-tooltip">
																<p className="regular-14 gray_dark_01">
																	Upload
																</p>
															</span>
														</label>
														<input
															type="file"
															className="file-upload d-none"
															name="templateUpload"
															id="templateUpload"
															onClick={(e) => (e.target.value = null)}
															onChange={(e) => onUploadFile(e)}
														/>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</LoaderComponent>
		</ModalContainer>
	);
};
export default AddFilesModal;
